import { Dialog } from "@kobalte/core/dialog";
import { TextField } from "@kobalte/core/text-field";
import { DeleteViewRequestConfig, useStorytellClient } from "@storytell/client";
import { dispatchGlobalEvent } from "@storytell/events";
import type { Asset, Concept, ConceptId, ViewId } from "@storytell/types";
import { StButton } from "@storytell/ui/buttons";
import { TbApps, TbEdit, TbFileText, TbFilter, TbFilterOff, TbMessage2, TbTrash, TbX } from "solid-icons/tb";
import { type Accessor, For, Match, type Setter, Show, Switch } from "solid-js";
import { useWire } from "~/wire/WireProvider";
import { NavigationConceptsPane } from "../concepts/NavigationConceptsPane";
import { Filters } from "../filtering/Filters";
import type { FilterBuilder } from "../filtering/filterBuilder";
import { NavigationAssetsPane } from "../layouts/minimal/NavigationAssetsPane";
import { KnowledgePaneViews } from "./KnowledgePaneViews";
import type { EntitySelection } from "./useEntitySelection";

export const KnowledgePaneContent = (props: {
  filterBuilder: FilterBuilder<Asset | Concept>;
  assetsSelection: EntitySelection;
  conceptsSelection: EntitySelection;
  search: Accessor<string>;
  setSearch: Setter<string>;
  filterExpanded: Accessor<boolean>;
  setFilterExpanded: Setter<boolean>;
  hasViewChanges: boolean;
  selectedConcept: ConceptId | "";
  setSelectedConcept: (conceptId: ConceptId | "") => void;
  editView: boolean;
  setEditView: Setter<boolean>;
}) => {
  const wire = useWire();
  const { knowledgeUi } = wire;

  return (
    <>
      <Show when={props.selectedConcept === ""}>
        <div class="flex items-center justify-between px-2 border-b border-primary-border pb-2 max-w-full">
          <div class="flex items-center gap-2 flex-auto overflow-x-auto scrollbar-hide">
            <KnowledgePaneViews
              hasViewChanges={props.hasViewChanges}
              filterBuilder={props.filterBuilder}
              assetsSelection={props.assetsSelection}
              conceptsSelection={props.conceptsSelection}
              editView={props.editView}
              setEditView={props.setEditView}
            />
          </div>
          <Dialog.CloseButton as={StButton} class="shrink-0">
            <TbX />
          </Dialog.CloseButton>
        </div>

        <div class="px-2 border-b border-primary-border max-w-full py-1.5 mb-1.5">
          <Filters
            activeView={knowledgeUi.activeView()!}
            builder={props.filterBuilder}
            hasViewChanges={props.hasViewChanges}
            editView={props.editView}
            setEditView={props.setEditView}
          />
        </div>

        <Show when={knowledgeUi.activeView() !== "new" && !props.editView}>
          <div class="px-2 pb-1.5 mb-1.5 flex items-center border-b border-primary-border">
            <button
              type="button"
              onClick={() => {
                dispatchGlobalEvent("add-items-to-prompt-scope", {
                  ids: knowledgeUi.activeView() !== "all" ? [knowledgeUi.activeView() as ViewId] : [],
                  allKnowledge: knowledgeUi.activeView() === "all",
                });
              }}
              class="text-sm mr-1 text-left cursor-pointer font-medium text-primary-text max-w-full line-clamp-1 wrap-anywhere py-1 px-2 border rounded-lg border-primary-border transition-all"
            >
              Chat with{" "}
              <span class="text-transparent bg-gradient-to-r from-violet-600 to-blue-600 dark:from-violet-400 dark:to-blue-400 bg-clip-text">
                @
                {knowledgeUi.activeView() === "all"
                  ? "All Knowledge"
                  : wire.sync.views.get(knowledgeUi.activeView() as ViewId)?.name}
              </span>
            </button>
            <Show when={knowledgeUi.activeView() !== "new" && knowledgeUi.activeView() !== "all"}>
              <StButton
                class="text-secondary-text mr-1"
                iconClass="size-3.5"
                noBorder
                icon={TbEdit}
                onClick={() => {
                  props.setEditView(true);
                }}
              />
              <StButton
                onClick={async () => {
                  if (!window.confirm("Are you sure you want to delete this view?")) {
                    return;
                  }
                  const client = useStorytellClient();
                  const request = DeleteViewRequestConfig.withBearerToken(wire.auth.token());
                  const resp = await client.fetch(request, { id: knowledgeUi.activeView() as ViewId }, undefined);
                  if (resp.isOk()) {
                    wire.knowledgeUi.setActiveView("all");
                    wire.sync.views.remove(knowledgeUi.activeView() as ViewId);
                  }
                }}
                class="text-secondary-text"
                iconClass="size-3.5"
                noBorder
                icon={TbTrash}
              />
            </Show>
          </div>
        </Show>

        <div class="px-4 flex items-center justify-between mb-2">
          <div class="flex items-center gap-4">
            <span class="text-sm">Viewing as</span>

            <div class="flex items-center gap-2 relative">
              <div
                class="absolute left-0 top-0 bottom-0 w-24 bg-tile rounded-lg transition-all duration-200 border border-primary-border"
                style={{
                  transform: `translateX(${knowledgeUi.activeTab() === "concepts" ? 0 : 96 + 1 + 2 * 8}px)`,
                }}
              />
              <For each={[{ id: "concepts", icon: TbApps }, "|", { id: "assets", icon: TbFileText }] as const}>
                {(tab) => (
                  <Show when={tab !== "|" && tab} fallback={<div class="w-px h-3 bg-primary-border" />}>
                    {(tab) => (
                      <StButton
                        onClick={() => knowledgeUi.setActiveTab(tab().id)}
                        data-active={tab().id === knowledgeUi.activeTab()}
                        icon={tab().icon}
                        noColor
                        noBorder
                        class="capitalize focus:ring-0 relative w-24 data-[active=true]:font-semibold"
                      >
                        {tab().id}
                      </StButton>
                    )}
                  </Show>
                )}
              </For>
            </div>
          </div>
          <TextField value={props.search()} onChange={props.setSearch}>
            <TextField.Label class="sr-only">Search</TextField.Label>
            <TextField.Input
              class="w-64 px-3 py-1 border border-primary-border rounded-lg bg-tile text-sm focus:outline-none focus:ring-2 focus:ring-brand"
              placeholder="Search"
            />
          </TextField>
        </div>
      </Show>

      <div class="">
        <Switch>
          <Match when={knowledgeUi.activeTab() === "concepts"}>
            <NavigationConceptsPane
              selectedConcept={props.selectedConcept}
              setSelectedConcept={props.setSelectedConcept}
              selection={props.conceptsSelection}
              search={props.search()}
              filterBuilder={props.filterBuilder}
            />
          </Match>
          <Match when={knowledgeUi.activeTab() === "assets"}>
            <NavigationAssetsPane
              selection={props.assetsSelection}
              search={props.search()}
              setSearch={props.setSearch}
              buildFilterFunction={props.filterBuilder.buildFilterFunction()}
              setLabelFilter={() => {}}
              assetToHighlight=""
              setAssetToHighlight={() => {}}
              labelToFilterBy=""
              setLabelToFilterBy={() => {}}
            />
          </Match>
        </Switch>
      </div>
    </>
  );
};
